import { useState, useMemo } from 'react';
import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import {
  usePlanPrices,
  useAddSubscriptionCheckout,
} from '../../hooks/useBilling';
import {
  PLAN_DESCRIPTIONS,
  PLAN_ORDER_BY_PLATFORM,
  type PlanName,
  type SignupPlatform,
} from '../../utils/planDescriptions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const PLATFORMS: SignupPlatform[] = ['UniFi', 'UISP'];

/** UniFi default stays P25 (the most-bought tier); UISP starts on its smallest tier. */
const DEFAULT_PLAN: Record<SignupPlatform, PlanName> = { UniFi: 'P25', UISP: 'U25' };

function formatCurrency(amount: number, currency: string): string {
  // Backend already converts Stripe cents to dollars
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

const pill = (active: boolean) =>
  `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
    active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
  }`;

export default function AddSubscriptionModal({ isOpen, onClose }: Props) {
  const [chosenPlatform, setPlatform] = useState<SignupPlatform>('UniFi');
  const [selectedPlan, setSelectedPlan] = useState<string>(DEFAULT_PLAN.UniFi);
  const [interval, setInterval] = useState<'monthly' | 'annual'>('monthly');

  const { data: planData, isLoading, isError } = usePlanPrices();
  const checkout = useAddSubscriptionCheckout();

  // Group plans by name for easy lookup
  const plans = planData?.plans;
  const plansByName = useMemo(() => {
    if (!plans) return {};
    return plans.reduce(
      (acc, plan) => {
        if (!acc[plan.plan_name]) {
          acc[plan.plan_name] = {};
        }
        acc[plan.plan_name][plan.interval] = plan;
        return acc;
      },
      {} as Record<string, Record<string, (typeof plans)[0]>>
    );
  }, [plans]);

  // A platform is offered only once at least one of its plans is priced.
  const platforms = useMemo(
    () => PLATFORMS.filter((p) => PLAN_ORDER_BY_PLATFORM[p].some((n) => plansByName[n])),
    [plansByName]
  );
  // Guard: if the chosen platform is not offered, show UniFi.
  const platform: SignupPlatform = platforms.includes(chosenPlatform) ? chosenPlatform : 'UniFi';

  // The chosen platform's ladder, minus plans the API has no price for (UISP rows arrive
  // with portal-api Part A; until portal-api lists a priced U plan the UISP choice is hidden).
  const availablePlans = useMemo(() => {
    return PLAN_ORDER_BY_PLATFORM[platform].filter((name) => plansByName[name]);
  }, [platform, plansByName]);

  // Switching platform lands on that platform's first priced plan (its default when priced).
  const handlePlatformChange = (next: SignupPlatform) => {
    if (next === platform) return;
    setPlatform(next);
    const ladder = PLAN_ORDER_BY_PLATFORM[next];
    const priced = ladder.filter((name) => plansByName[name]);
    setSelectedPlan(priced.includes(DEFAULT_PLAN[next]) ? DEFAULT_PLAN[next] : (priced[0] ?? DEFAULT_PLAN[next]));
  };

  // Get current selected price
  const selectedPrice = plansByName[selectedPlan]?.[interval];

  const handleSubmit = () => {
    if (!selectedPrice) return;
    checkout.mutate({
      plan_name: selectedPlan,
      interval,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Subscription">
      <div className="space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-800">
              Failed to load plan prices. Please try again or contact support.
            </p>
          </div>
        ) : (
          <>
            {/* Platform Toggle: UniFi (Network controllers) or UISP */}
            {platforms.length > 1 && (
            <div className="flex justify-center">
              <div className="inline-flex rounded-lg bg-gray-100 p-1" role="group" aria-label="Platform">
                {platforms.map((p) => (
                  <button key={p} type="button" aria-pressed={platform === p} onClick={() => handlePlatformChange(p)} className={pill(platform === p)}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Interval Toggle */}
            <div className="flex justify-center">
              <div className="inline-flex rounded-lg bg-gray-100 p-1" role="group" aria-label="Billing interval">
                <button type="button" aria-pressed={interval === 'monthly'} onClick={() => setInterval('monthly')} className={pill(interval === 'monthly')}>
                  Monthly
                </button>
                <button type="button" aria-pressed={interval === 'annual'} onClick={() => setInterval('annual')} className={pill(interval === 'annual')}>
                  Annual
                </button>
              </div>
            </div>

            {/* Plan Selection */}
            <div className="space-y-2">
              {availablePlans.map((planName) => {
                const price = plansByName[planName]?.[interval];
                if (!price) return null;

                return (
                  <label
                    key={planName}
                    className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedPlan === planName
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="plan"
                        value={planName}
                        checked={selectedPlan === planName}
                        onChange={() => setSelectedPlan(planName)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          {planName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {PLAN_DESCRIPTIONS[planName]}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        {formatCurrency(price.amount, price.currency)}
                      </div>
                      <div className="text-sm text-gray-500">
                        /{interval === 'monthly' ? 'mo' : 'yr'}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {platform === 'UISP' && (
              <p className="text-sm text-gray-500">
                UISP plans run Ubiquiti&apos;s UISP for airMAX, EdgeRouter and UFiber networks. More than 1,500 devices? Contact us.
              </p>
            )}

            {checkout.isError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">
                  Failed to create checkout session. Please try again or contact
                  support.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                disabled={checkout.isPending}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={checkout.isPending || !selectedPrice}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {checkout.isPending ? (
                  <>
                    <Spinner
                      size="sm"
                      className="border-white border-t-transparent"
                    />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Continue to Checkout'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
